import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
  type Firestore,
} from 'firebase/firestore';

export type ProjectNotificationMode = 'all' | 'mentions' | 'digest' | 'muted';

export const PROJECT_NOTIFICATION_MODE_OPTIONS: Array<{
  id: ProjectNotificationMode;
  label: string;
  hint: string;
}> = [
  { id: 'all', label: 'All', hint: 'Every project notification (current default)' },
  {
    id: 'mentions',
    label: 'Mentions only',
    hint: '@mentions and direct assignments still come through live',
  },
  {
    id: 'digest',
    label: 'Digest',
    hint: 'Non-mention activity rolls into one daily summary; mentions stay live',
  },
  {
    id: 'muted',
    label: 'Muted',
    hint: 'Suppress everything except explicit @mentions',
  },
];

export function normalizeProjectNotificationMode(raw: unknown): ProjectNotificationMode {
  const v = String(raw || '')
    .trim()
    .toLowerCase();
  if (v === 'mentions' || v === 'mentions_only') return 'mentions';
  if (v === 'digest') return 'digest';
  if (v === 'muted' || v === 'mute') return 'muted';
  return 'all';
}

export function projectNotificationPrefsDocPath(userId: string, projectId: string): string {
  return `users/${userId}/projectNotificationPrefs/${projectId}`;
}

export type ProjectNotificationPrefRow = {
  projectId: string;
  mode: ProjectNotificationMode;
  projectName?: string | null;
  updatedAt?: Date | null;
};

export async function loadProjectNotificationPref(
  db: Firestore,
  userId: string,
  projectId: string,
): Promise<ProjectNotificationMode> {
  const snap = await getDoc(doc(db, 'users', userId, 'projectNotificationPrefs', projectId));
  if (!snap.exists()) return 'all';
  return normalizeProjectNotificationMode(snap.data()?.mode);
}

export async function saveProjectNotificationPref(args: {
  db: Firestore;
  userId: string;
  projectId: string;
  mode: ProjectNotificationMode;
  projectName?: string | null;
}): Promise<void> {
  const ref = doc(args.db, 'users', args.userId, 'projectNotificationPrefs', args.projectId);
  if (args.mode === 'all') {
    // Default — drop the doc so Settings only lists intentional changes.
    try {
      await deleteDoc(ref);
    } catch {
      await setDoc(
        ref,
        {
          mode: 'all',
          projectId: args.projectId,
          projectName: args.projectName || null,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    }
    return;
  }
  await setDoc(
    ref,
    {
      mode: args.mode,
      projectId: args.projectId,
      projectName: args.projectName || null,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

export async function listChangedProjectNotificationPrefs(
  db: Firestore,
  userId: string,
): Promise<ProjectNotificationPrefRow[]> {
  const snap = await getDocs(collection(db, 'users', userId, 'projectNotificationPrefs'));
  const rows: ProjectNotificationPrefRow[] = [];
  snap.forEach((d) => {
    const data = d.data();
    const mode = normalizeProjectNotificationMode(data.mode);
    if (mode === 'all') return;
    rows.push({
      projectId: d.id,
      mode,
      projectName: typeof data.projectName === 'string' ? data.projectName : null,
      updatedAt: data.updatedAt?.toDate?.() || null,
    });
  });
  rows.sort((a, b) => (a.projectName || a.projectId).localeCompare(b.projectName || b.projectId));
  return rows;
}
